import { create } from "zustand";

export interface TombstoneItem {
  id?: string;
  tombstoneId?: string;
  tombstoneNumber?: string;
  iceClaimNumber?: string;
  status?: string;

  formDate: string;
  branchName: string;
  consultantName: string;
  policyNumber: string;
  invoiceNumber: string;
  supplementaryName: string;
  cashBenefitValue: string;
  serviceBenefitValue: string;
  cashPayoutOnly: boolean;
  serviceRequired: boolean;
  isUnderWaitingPeriod: boolean;

  clientFullName: string;
  clientSurname: string;
  clientNames: string;
  clientResidentialAddress: string;
  clientContactNumbers: string;

  deceasedFullName: string;
  deceasedSurname: string;
  deceasedNames: string;
  deceasedDateOfBirth: string;
  deceasedDateOfDeath: string;
  tombstoneMessage: string;

  stoneType: string;
  stoneCode: string;
  stonePrice: number | "";
  kilometerCharge: number | "";
  totalPrice: number | "";

  installationDate: string;
  unveilingDate: string;
  cemeteryName: string;
  installationPlace: string;
  graveNumber: string;
  accessNotes: string;
  internalNotes: string;
  clientNotes: string;

  accountName: string;
  bank: string;
  accountNumber: string;
  branchCode: string;
  reference: string;

  missingFieldsFlag?: boolean;
  missingFields?: string[] | null;
  [key: string]: any;
}

interface TombstoneState {
  tombstones: TombstoneItem[];
  totalCount: number;
  statusCounts: Record<string, number>;
  selectedTombstone: TombstoneItem | null;

  setTombstones: (data: any) => void;
  setSelectedTombstone: (item: any) => void;
  updateSelectedTombstone: (data: Partial<TombstoneItem>) => void;
  clearSelectedTombstone: () => void;
}

const defaultTombstone: TombstoneItem = {
  formDate: "",
  branchName: "",
  consultantName: "",
  policyNumber: "",
  invoiceNumber: "",
  supplementaryName: "",
  cashBenefitValue: "",
  serviceBenefitValue: "",
  cashPayoutOnly: false,
  serviceRequired: false,
  isUnderWaitingPeriod: false,
  clientFullName: "",
  clientSurname: "",
  clientNames: "",
  clientResidentialAddress: "",
  clientContactNumbers: "",
  deceasedFullName: "",
  deceasedSurname: "",
  deceasedNames: "",
  deceasedDateOfBirth: "",
  deceasedDateOfDeath: "",
  tombstoneMessage: "",
  stoneType: "",
  stoneCode: "",
  stonePrice: "",
  kilometerCharge: "",
  totalPrice: "",
  installationDate: "",
  unveilingDate: "",
  cemeteryName: "",
  installationPlace: "",
  graveNumber: "",
  accessNotes: "",
  internalNotes: "",
  clientNotes: "",
  accountName: "",
  bank: "",
  accountNumber: "",
  branchCode: "",
  reference: "",
};

const joinName = (first?: string, last?: string) =>
  [first, last].filter(Boolean).join(" ").trim();

const splitFullName = (name?: string | null) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return { firstName: "", surname: "" };
  }

  if (parts.length === 1) {
    return { firstName: parts[0], surname: "" };
  }

  return {
    firstName: parts.slice(0, -1).join(" "),
    surname: parts[parts.length - 1],
  };
};

const toBoolean = (value: any) => value === true || value === "true";

const resolveList = (data: any): TombstoneItem[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.tombstones)) return data.tombstones;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const buildStatusCounts = (items: TombstoneItem[]) =>
  items.reduce<Record<string, number>>((acc, item) => {
    const status = getTombstoneStatus(item);
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

export const normalizeTombstone = (item: any): TombstoneItem => {
  const basic = item?.basicInformation || {};
  const stone = item?.stoneDetails || {};
  const cemetery = item?.cemeteryAndInstallation || {};
  const banking = item?.bankingDetails || {};
  const clientNameParts = splitFullName(item?.clientFullName);
  const deceasedNameParts = splitFullName(item?.deceasedFullName);
  const clientNames = basic.clientNames ?? item?.clientNames ?? clientNameParts.firstName;
  const clientSurname =
    basic.clientSurname ?? item?.clientSurname ?? clientNameParts.surname;
  const deceasedNames =
    basic.deceasedNames ?? item?.deceasedNames ?? deceasedNameParts.firstName;
  const deceasedSurname =
    basic.deceasedSurname ?? item?.deceasedSurname ?? deceasedNameParts.surname;

  return {
    ...defaultTombstone,
    ...item,
    id: item?.tombstoneId || item?.id || "",
    tombstoneId: item?.tombstoneId || item?.id || "",
    tombstoneNumber: item?.tombstoneNumber || "",
    iceClaimNumber: item?.iceClaimNumber || "",
    status: item?.status || "DRAFT",

    formDate: basic.formDate ?? item?.formDate ?? "",
    branchName: basic.branchName ?? item?.branchName ?? "",
    consultantName: basic.consultantName ?? item?.consultantName ?? "",
    policyNumber: basic.policyNumber ?? item?.policyNumber ?? "",
    invoiceNumber: basic.invoiceNumber ?? item?.invoiceNumber ?? "",
    supplementaryName: basic.supplementaryName ?? item?.supplementaryName ?? "",
    cashBenefitValue: String(
      basic.cashBenefitValue ?? item?.cashBenefitValue ?? "",
    ),
    serviceBenefitValue: String(
      basic.serviceBenefitValue ?? item?.serviceBenefitValue ?? "",
    ),
    cashPayoutOnly: toBoolean(basic.cashPayoutOnly ?? item?.cashPayoutOnly),
    serviceRequired: toBoolean(basic.serviceRequired ?? item?.serviceRequired),
    isUnderWaitingPeriod: toBoolean(
      basic.isUnderWaitingPeriod ?? item?.isUnderWaitingPeriod,
    ),

    clientNames,
    clientSurname,
    clientFullName:
      item?.clientFullName || joinName(clientNames, clientSurname) || "",
    clientResidentialAddress:
      basic.clientResidentialAddress ?? item?.clientResidentialAddress ?? "",
    clientContactNumbers:
      basic.clientContactNumbers ?? item?.clientContactNumbers ?? "",

    deceasedNames,
    deceasedSurname,
    deceasedFullName:
      item?.deceasedFullName ||
      joinName(deceasedNames, deceasedSurname) ||
      "",
    deceasedDateOfBirth:
      basic.deceasedDateOfBirth ?? item?.deceasedDateOfBirth ?? "",
    deceasedDateOfDeath:
      basic.deceasedDateOfDeath ?? item?.deceasedDateOfDeath ?? "",
    tombstoneMessage: basic.tombstoneMessage ?? item?.tombstoneMessage ?? "",

    stoneType: stone.stoneType ?? item?.stoneType ?? "",
    stoneCode: stone.stoneCode ?? item?.stoneCode ?? "",
    stonePrice: stone.stonePrice ?? item?.stonePrice ?? "",
    kilometerCharge: stone.kilometerCharge ?? item?.kilometerCharge ?? "",
    totalPrice: stone.totalPrice ?? item?.totalPrice ?? "",

    installationDate:
      cemetery.installationDate ?? item?.installationDate ?? "",
    unveilingDate: cemetery.unveilingDate ?? item?.unveilingDate ?? "",
    cemeteryName: cemetery.cemeteryName ?? item?.cemeteryName ?? "",
    installationPlace:
      cemetery.installationPlace ?? item?.installationPlace ?? "",
    graveNumber: cemetery.graveNumber ?? item?.graveNumber ?? "",
    accessNotes: cemetery.accessNotes ?? item?.accessNotes ?? "",
    internalNotes: cemetery.internalNotes ?? item?.internalNotes ?? "",
    clientNotes: cemetery.clientNotes ?? item?.clientNotes ?? "",

    accountName: banking.accountName ?? item?.accountName ?? "",
    bank: banking.bank ?? item?.bank ?? "",
    accountNumber: banking.accountNumber ?? item?.accountNumber ?? "",
    branchCode: banking.branchCode ?? item?.branchCode ?? "",
    reference: banking.reference ?? item?.reference ?? "",
    missingFieldsFlag: item?.missingFieldsFlag ?? false,
    missingFields: item?.missingFields ?? null,
  };
};

export const getTombstoneStatus = (item: Partial<TombstoneItem> | null) =>
  String(item?.status || "DRAFT").toUpperCase();

export const getTombstoneId = (item: Partial<TombstoneItem> | null) =>
  item?.tombstoneId || item?.id || item?.tombstoneNumber || "";

export const useTombstoneStore = create<TombstoneState>((set) => ({
  tombstones: [],
  totalCount: 0,
  statusCounts: {},
  selectedTombstone: null,

  setTombstones: (data: any) => {
    const items = resolveList(data).map(normalizeTombstone);

    set({
      tombstones: items,
      totalCount:
        data?.totalCount ??
        data?.totalElements ??
        data?.total ??
        data?.count ??
        items.length,
      statusCounts: data?.statusCounts || buildStatusCounts(items),
    });
  },

  setSelectedTombstone: (item: any) =>
    set({
      selectedTombstone: normalizeTombstone(item),
    }),

  updateSelectedTombstone: (data) =>
    set((state) => ({
      selectedTombstone: state.selectedTombstone
        ? {
            ...state.selectedTombstone,
            ...data,
          }
        : {
            ...defaultTombstone,
            ...data,
          },
    })),

  clearSelectedTombstone: () =>
    set({
      selectedTombstone: null,
    }),
}));
